<template>
  <section
    class="slider-container sizes-container flex flex-col justify-between rounded-md border border-light-gray p-4 text-left"
  >
    <div>
      <h1 class="text-dark-blue">Size</h1>
      <p class="mt-2 text-xs text-body-color">
        Size is based on the broker's total gross revenue.*
      </p>
    </div>
    <div
      v-if="selectedSizeDetails.label !== 'Unknown'"
      class="flex flex-col items-center"
    >
      <h1 class="text-base text-dark-blue">{{ selectedSizeDetails.label }}</h1>
      <p
        v-if="selectedSizeDetails.label !== 'Unknown'"
        class="text-center text-2xs text-body-color"
      >
        This broker grosses at least {{ selectedSizeDetails.subtitle }}
      </p>
      <p
        v-if="selectedSizeDetails.label !== 'Unknown'"
        class="text-center text-2xs text-body-color"
      >
        in revenue annually
      </p>
    </div>
    <section
      v-if="selectedSizeDetails.label == 'Unknown'"
      class="mt-4 flex h-full flex-col items-center justify-center gap-3 rounded-md border border-light-gray bg-off-white p-4"
      style="min-height: 172px"
    >
      <div class="flex items-center gap-2">
        <h1 class="text-base text-body-color">Unknown</h1>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="15"
          viewBox="0 0 14 15"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M14 7.39062C14 11.2566 10.866 14.3906 7 14.3906C3.13401 14.3906 0 11.2566 0 7.39062C0 3.52463 3.13401 0.390625 7 0.390625C10.866 0.390625 14 3.52463 14 7.39062ZM7.0595 2.52351C7.95291 2.47575 8.83717 2.7773 9.41463 3.52435C9.93112 4.19252 9.92621 4.95515 9.75864 5.55618C9.5962 6.1388 9.26307 6.66291 8.9475 7.05733L8.40562 7.7346C8.14305 8.06278 8 8.47056 8 8.89085H6C6 8.01638 6.29764 7.16795 6.84395 6.48513L7.38583 5.80785C7.6097 5.52804 7.76901 5.24541 7.83212 5.01904C7.88813 4.81813 7.84687 4.76598 7.83356 4.74915L7.83227 4.74751C7.73732 4.62468 7.54762 4.50027 7.16628 4.52066C6.77252 4.54172 6.22037 4.72535 5.59999 5.19062L4.40001 3.59061C5.25515 2.94927 6.17852 2.57062 7.0595 2.52351ZM5.99992 10.1363V12.3495H7.99992V10.1363H5.99992Z"
            fill="#A9ADBD"
          />
        </svg>
      </div>
    </section>
    <div
      v-else
      style="margin-bottom: 12px"
    >
      <div class="relative mt-4 flex w-full items-start justify-center px-4">
        <slider
          v-model="animatedRange"
          :lazy="sliderLazy"
          :min="0"
          :max="150"
          :options="sliderOptions"
          class="flex w-full"
          :disabled="true"
        />
        <div
          class="absolute inset-0 mx-4 flex flex-row items-start justify-between"
          style="top: -5px"
        >
          <div
            v-for="(dot, index) in sizeDetails"
            :key="`ma-slider-dot-${index}`"
            :class="{
              'yellow-shadow bg-yellow-500':
                selectedSizeDetails.exportValue !== 'unknown' &&
                (index === 0 || dot.exportValue <= range),
              'bg-light-gray':
                selectedSizeDetails.exportValue === 'unknown' ||
                dot.exportValue > range,
            }"
            class="z-10 h-3 w-3 rounded-full"
          />
        </div>
      </div>
      <div class="flex h-5 flex-row items-end justify-between">
        <div
          v-for="(dot, index) in sizeDetails"
          :key="`slider-dot-label-${index}`"
          class="h-4 w-9"
        >
          <div
            class="flex flex-col whitespace-nowrap"
            :class="[
              dot.label == selectedSizeDetails.label
                ? 'text-dark-blue'
                : 'text-placeholder-gray',
            ]"
          >
            <span
              class="text-xs"
              :class="{
                'ml-1': dot.label == 'Small',
                '-ml-2': dot.label == 'Medium',
                '-ml-1': dot.label == 'Mega',
              }"
              >{{ dot.label }}</span
            >
            <span
              class="text-center text-2xs"
              :class="{
                'ml-2': dot.subtitle == '$0M+',
                '-ml-1': dot.subtitle == '$10M+' || dot.subtitle == '$500M+',
              }"
              >{{ dot.subtitle }}</span
            >
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import Slider from '@vueform/slider';
import { ref, computed, onMounted, defineProps, onBeforeUnmount } from 'vue';
const props = defineProps({
  size: {
    type: String,
    required: true,
  },
});
let observer = null;
const range = ref(-1);
const sliderLazy = ref(true);
const animatedRange = ref(0);
const sliderVisible = ref(false);

const animateSlider = () => {
  if (range.value < 0) return;

  let currentValue = 0;
  const step = 5;
  const interval = setInterval(() => {
    if (currentValue >= range.value) {
      clearInterval(interval);
      return;
    }
    currentValue += step;
    animatedRange.value = currentValue;
  }, 10);
};

const handleIntersection = (entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      sliderVisible.value = true;
      animateSlider();
    }
  });
};

onMounted(() => {
  convertSizeToRange();

  if (props.size == 'unknown') return;

  observer = new IntersectionObserver(handleIntersection, {
    threshold: 1,
  });

  const targetElement = document.querySelector('.slider-container');
  if (targetElement) {
    observer.observe(targetElement);
  }
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
});

const selectedSizeDetails = computed(() => {
  return (
    sizeDetails.value.find((dot) => dot.exportValue === range.value) || {
      label: 'Unknown',
      subtitle: 'unknown',
      exportValue: 'unknown',
    }
  );
});

const sizeDetails = computed(() => [
  { label: 'Small', subtitle: '$0M+', exportValue: 0 },
  {
    label: 'Medium',
    subtitle: '$10M+',
    exportValue: 50,
  },
  {
    label: 'Large',
    subtitle: '$50M+',
    exportValue: 100,
  },
  {
    label: 'Mega',
    subtitle: '$500M+',
    exportValue: 150,
  },
]);
const sliderOptions = ref({
  step: 50,
  min: 0,
  max: 150,
  tooltips: false,
});

const convertSizeToRange = () => {
  if (!props.size) return;

  switch (props.size) {
    case 'small':
      range.value = 0;
      break;
    case 'medium':
      range.value = 50;
      break;
    case 'large':
      range.value = 100;
      break;
    case 'mega':
      range.value = 150;
      break;
    default:
      range.value = -1;
  }
};
</script>
<style src="@vueform/slider/themes/default.css"></style>
<style>
:root {
  --slider-bg: #ededf0;
  --slider-connect-bg: #f8c617;
  --slider-handle-bg: rgb(248, 198, 23);
  --slider-connect-bg-disabled: rgb(248, 198, 23);
  --slider-handle-shadow: 0.5px 0.5px 2px 1px rgba(248, 198, 23, 0.32);
  --slider-handle-shadow-active: 0.5px 0.5px 2px 1px rgba(248, 198, 23, 0.42);
  --slider-handle-ring-width: 3px;
  --slider-handle-ring-color: #f8c61730;
  cursor: default;
}

.slider-base {
  height: 2px !important;
  box-shadow: 0px 5px 10px rgba(248, 198, 23, 0.3) !important;
}

.slider-handle {
  display: none !important;
}

.slider[disabled],
.slider[disabled] .slider-handle {
  cursor: default !important;
}

[disabled].slider-handle,
[disabled] .slider-handle,
[disabled].slider-target,
[disabled] .slider-target {
  cursor: default !important;
}

.slider-connect {
  cursor: default;
}

.sizes-container .vcp.vcp--expandable {
  cursor: pointer;
}

.sizes-container .vcp.vcp--expandable > .vcp__header:hover {
  background: #f7f7fa;
}

.sizes-container .vcp.vcp--expandable > .vcp__header {
  margin-left: -0.75rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  width: calc(100% + 1.5rem);
}

.sizes-container
  .vcp.vcp--expandable:not(.vcp--expanded)
  > .vcp__header
  > .vcp__header-icon.caret-rotate {
  transform: rotate(-90deg) !important;
}

.sizes-container
  .vcp.vcp--expandable.vcp--expanded
  > .vcp__header
  > .vcp__header-icon.caret-rotate {
  transform: rotate(0deg) !important;
}

.sizes-container .vcp__body {
  margin-bottom: 1rem;
}

.reverse {
  --slider-bg: #f8c617;
  --slider-connect-bg: #ededf0;
}

.reverseBlue {
  --slider-bg: #6187b0;
  --slider-connect-bg: #ededf0;
}

.yellow-shadow {
  box-shadow: 0px 5px 10px 0px rgba(248, 198, 23, 0.3);
}
</style>
